import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Trash2, HardDrive, Video, FileText, File, Image, Database } from "lucide-react";

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function Armazenamento() {
  const [activeTab, setActiveTab] = useState("overview");

  const { data: stats, refetch: refetchStats } = trpc.armazenamento.stats.useQuery();

  const categorias = [
    { key: 'videos', label: 'Vídeos MECOM', icon: Video },
    { key: 'notas', label: 'Notas XML', icon: FileText },
    { key: 'danfes', label: 'DANFEs (PDF)', icon: File },
    { key: 'boletos', label: 'Boletos', icon: File },
    { key: 'relatorios', label: 'Relatórios', icon: FileText },
    { key: 'uploads', label: 'Uploads Gerais', icon: Image },
  ];

  const totalBytes = stats ? Object.values(stats as Record<string, any>).reduce((acc: number, cat: any) => acc + (cat?.totalBytes || 0), 0) : 0;
  const freeBytes = stats ? (stats as any)._disk?.freeBytes ?? 0 : 0;

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Armazenamento do Servidor</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><HardDrive className="w-4 h-4" />Total Usado</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{formatBytes(totalBytes as number)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><Database className="w-4 h-4" />Espaço Livre</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{formatBytes(freeBytes)}</p></CardContent>
        </Card>
        {categorias.map(cat => {
          const catStats = stats ? (stats as any)[cat.key] : null;
          return (
            <Card key={cat.key}>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><cat.icon className="w-4 h-4" />{cat.label}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-lg font-bold">{formatBytes(catStats?.totalBytes || 0)}</p>
                <p className="text-xs text-muted-foreground">{catStats?.count || 0} arquivos</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          {categorias.map(cat => (
            <TabsTrigger key={cat.key} value={cat.key}>{cat.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <div className="mt-4 text-muted-foreground">Selecione uma categoria acima para visualizar e gerenciar os arquivos.</div>
        </TabsContent>
        {categorias.map(cat => (
          <TabsContent key={cat.key} value={cat.key}>
            <FileListTab categoria={cat.key} label={cat.label} onDelete={() => refetchStats()} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function FileListTab({ categoria, label, onDelete }: { categoria: string; label: string; onDelete: () => void }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'modifiedAt'>('modifiedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [videoCorrompido, setVideoCorrompido] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const limit = 50;

  const queryParams = {
    categoria,
    page,
    limit,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    sortBy,
    sortDir,
    videoCorrompido: categoria === 'videos' && videoCorrompido ? true : undefined,
  };

  const { data, refetch } = trpc.armazenamento.listFiles.useQuery(queryParams);
  const { refetch: refetchAllPaths } = trpc.armazenamento.getAllPaths.useQuery(
    { categoria, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, videoCorrompido: categoria === 'videos' && videoCorrompido ? true : undefined },
    { enabled: false },
  );

  const deleteMut = trpc.armazenamento.deleteFiles.useMutation({
    onSuccess: (result) => {
      toast.success(`${result.deleted} arquivo(s) deletado(s).`);
      setSelected(new Set());
      refetch();
      onDelete();
    },
    onError: (err) => {
      toast.error(err.message, { duration: 5000 });
    },
  });

  const files = (data as any)?.files || [];
  const total = (data as any)?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const toggleSelect = (path: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const togglePageAll = () => {
    if (files.every((f: any) => selected.has(f.path))) {
      setSelected(prev => {
        const next = new Set(prev);
        files.forEach((f: any) => next.delete(f.path));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        files.forEach((f: any) => next.add(f.path));
        return next;
      });
    }
  };

  const handleSelectAll = async () => {
    setSelectingAll(true);
    try {
      const result = await refetchAllPaths();
      const paths: string[] = (result.data as any) || [];
      setSelected(new Set(paths));
      toast.success(`${paths.length} arquivo(s) selecionado(s).`);
    } catch {
      toast.error('Erro ao selecionar todos os arquivos.');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleDelete = () => {
    if (selected.size === 0) { toast.error("Nenhum arquivo selecionado."); return; }
    if (!confirm(`Tem certeza que deseja deletar ${selected.size} arquivo(s)? Esta ação é irreversível.`)) return;
    deleteMut.mutate({ paths: Array.from(selected) });
  };

  const handleSort = (col: 'name' | 'size' | 'modifiedAt') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (col: 'name' | 'size' | 'modifiedAt') => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const handleFilterChange = () => {
    setPage(1);
    setSelected(new Set());
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground">De:</label>
          <input
            type="date"
            className="border rounded px-2 py-1 text-sm"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); handleFilterChange(); }}
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground">Até:</label>
          <input
            type="date"
            className="border rounded px-2 py-1 text-sm"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); handleFilterChange(); }}
          />
        </div>
        {categoria === 'videos' && (
          <Button size="sm" variant={videoCorrompido ? 'default' : 'outline'} onClick={() => { setVideoCorrompido((ativo) => !ativo); handleFilterChange(); }}>
            Vídeo 0:00 / corrompido
          </Button>
        )}
        {(dateFrom || dateTo || videoCorrompido) && (
          <Button size="sm" variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); setVideoCorrompido(false); handleFilterChange(); }}>Limpar Filtro</Button>
        )}
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{total} arquivo(s) em {label}</span>
          <Button size="sm" variant="outline" onClick={handleSelectAll} disabled={selectingAll || total === 0}>
            {selectingAll ? 'Selecionando...' : `Selecionar Todos (${total})`}
          </Button>
        </div>
        <Button variant="destructive" size="sm" onClick={handleDelete} disabled={selected.size === 0 || deleteMut.isLoading}>
          <Trash2 className="w-4 h-4 mr-1" /> Deletar Selecionados ({selected.size})
        </Button>
      </div>

      <div className="border rounded-lg overflow-auto max-h-[500px]">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="p-2 w-8"><input type="checkbox" checked={files.length > 0 && files.every((f: any) => selected.has(f.path))} onChange={togglePageAll} /></th>
              <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('name')}>Nome{sortIndicator('name')}</th>
              <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('size')}>Tamanho{sortIndicator('size')}</th>
              <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('modifiedAt')}>Data{sortIndicator('modifiedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file: any) => (
              <tr key={file.path} className={`border-t ${selected.has(file.path) ? 'bg-red-50' : ''}`}>
                <td className="p-2"><input type="checkbox" checked={selected.has(file.path)} onChange={() => toggleSelect(file.path)} /></td>
                <td className="p-2 font-mono text-xs truncate max-w-[300px]" title={file.name}>{file.name}</td>
                <td className="p-2 text-xs">{formatBytes(file.size)}</td>
                <td className="p-2 text-xs">{file.modifiedAt ? new Date(file.modifiedAt).toLocaleString('pt-BR') : '-'}</td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Nenhum arquivo nesta categoria.</td></tr>}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-sm self-center">Página {page} de {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
        </div>
      )}
    </div>
  );
}
